import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AiSolutionSearchOption, AiSolutionSearchResult } from '@/app/interfaces/new-ai-solution-usage';
import { SearchService } from '@/app/services/ia-services/search.service';
import { AutoComplete, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ai-solution-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AutoCompleteModule],
  templateUrl: './ai-solution-search.component.html',
  styleUrl: './ai-solution-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AiSolutionSearchComponent),
      multi: true,
    },
  ],
})
export class AiSolutionSearchComponent implements ControlValueAccessor, OnDestroy {
  @Output() readonly selectionChange = new EventEmitter<AiSolutionSearchOption | null>();
  @ViewChild(AutoComplete) private autoComplete?: AutoComplete;

  readonly searchControl = new FormControl<string | AiSolutionSearchOption | null>('');
  readonly suggestions = signal<AiSolutionSearchOption[]>([]);
  readonly loading = signal(false);

  private selectedName: string | null = null;
  private searchRequestId = 0;
  private readonly valueSubscription: Subscription;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private searchService: SearchService) {
    this.valueSubscription = this.searchControl.valueChanges.subscribe((value) => {
      const name = this.getValueName(value);
      this.onChange(name);

      if (this.selectedName !== null && name !== this.selectedName) {
        this.selectedName = null;
        this.selectionChange.emit(null);
      }

      if (name.trim().length < 3) {
        this.searchRequestId++;
        this.suggestions.set([]);
        this.loading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.valueSubscription.unsubscribe();
  }

  writeValue(value: string | null): void {
    this.selectedName = null;
    this.searchControl.setValue(value ?? '', { emitEvent: false });
    this.suggestions.set([]);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
      return;
    }

    this.searchControl.enable({ emitEvent: false });
  }

  async onSearch(query: string): Promise<void> {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 3) {
      return;
    }

    const requestId = ++this.searchRequestId;
    this.loading.set(true);
    this.suggestions.set([]);

    try {
      const response = await this.searchService.searchAiSolutions(normalizedQuery);
      if (requestId !== this.searchRequestId || this.currentText().trim() !== normalizedQuery) {
        return;
      }

      this.suggestions.set(response.data.map((solution) => this.toExistingOption(solution)));
    } catch {
      if (requestId === this.searchRequestId && this.currentText().trim() === normalizedQuery) {
        this.suggestions.set([]);
      }
    } finally {
      if (requestId === this.searchRequestId) {
        this.loading.set(false);
      }
    }
  }

  onSuggestionSelected(event: AutoCompleteSelectEvent): void {
    const option = event.value as AiSolutionSearchOption;
    const selectedName = this.getOptionVisibleName(option);
    this.searchRequestId++;
    this.loading.set(false);
    this.suggestions.set([]);
    this.selectedName = selectedName;
    this.searchControl.setValue(selectedName, { emitEvent: false });
    this.onChange(selectedName);
    this.autoComplete?.hide();
    this.selectionChange.emit(option);
  }

  markAsTouched(): void {
    this.onTouched();
  }

  shouldShowAddAction(): boolean {
    return this.currentText().trim().length >= 3 && !this.loading() && this.suggestions().length === 0;
  }

  addCurrentTextAsNewSolution(): void {
    const name = this.currentText().trim();
    if (name.length < 3) {
      return;
    }

    const option = this.toNewOption(name);
    this.searchRequestId++;
    this.loading.set(false);
    this.suggestions.set([]);
    this.selectedName = name;
    this.searchControl.setValue(name, { emitEvent: false });
    this.onChange(name);
    this.autoComplete?.hide();
    this.selectionChange.emit(option);
  }

  private toExistingOption(solution: AiSolutionSearchResult): AiSolutionSearchOption {
    return {
      mode: 'existing',
      name: solution.nombre,
      description: solution.descripcion ?? '',
      type: solution.tipo,
      status: solution.estado,
    };
  }

  private toNewOption(name: string): AiSolutionSearchOption {
    return {
      mode: 'new',
      name,
      description: '',
    };
  }

  private currentText(): string {
    return this.getValueName(this.searchControl.value);
  }

  private getValueName(value: string | AiSolutionSearchOption | null): string {
    return typeof value === 'string' ? value : (value?.name ?? '');
  }

  private getOptionVisibleName(option: AiSolutionSearchOption & { nombre?: string }): string {
    return option.name ?? option.nombre ?? '';
  }
}
